import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Activity, Loader2, RefreshCw, Wifi, WifiOff, Signal, Radio } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vending`;

// v2.5.864 — Telemetry page (V2 preview)
// This page will show live MDB/DEX telemetry from Jetinno + Aurency machines
// once the ingestion webhooks are wired (Sprint V2). For now it shows the
// per-machine telemetry provider + connection state derived from
// `machine.telemetry_provider` and simulated last_seen timestamps.
export default function VendingTelemetryPage() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/machines`);
      setMachines(data.items || []);
    } catch (e) {
      if (e.response?.status !== 402) {
        toast.error(e.response?.data?.detail || 'Could not load telemetry');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const jetinno = machines.filter(m => m.telemetry_provider === 'jetinno');
  const aurency = machines.filter(m => m.telemetry_provider === 'aurency');
  const manual = machines.filter(m => m.telemetry_provider === 'manual' || m.telemetry_provider === 'other');

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="vending-telemetry-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-sky-600 flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" data-testid="vending-telemetry-title">
              Vending Telemetry<PageInfoTooltip pageKey="vending-telemetry" />
            </h1>
            <Badge className="bg-sky-100 text-sky-800 border-0 text-[10px] sm:text-xs">V2 · Preview</Badge>
          </div>
          <p className="text-xs sm:text-sm text-gray-medium">Real-time MDB/DEX event stream from Jetinno + Aurency fleets.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="h-9 text-xs sm:text-sm" data-testid="telemetry-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      <Card className="border-sky-200 bg-sky-50">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Radio className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-sky-900">Ingestion V2 rolling out</div>
              <div className="text-xs sm:text-sm text-sky-800 mt-1">
                HMAC-signed webhook endpoints for Jetinno (<code>/api/vending/telemetry/jetinno</code>) and Aurency
                (<code>/api/vending/telemetry/aurency</code>) are next up. Once your machines are pointed at your
                Konnect gateway, DEX/EVA-DTS and MDB events will stream in real time.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <ProviderCard label="Jetinno" count={jetinno.length} tone="emerald" icon={<Wifi className="h-5 w-5" />} />
        <ProviderCard label="Aurency" count={aurency.length} tone="sky" icon={<Signal className="h-5 w-5" />} />
        <ProviderCard label="Manual / Other" count={manual.length} tone="gray" icon={<WifiOff className="h-5 w-5" />} />
      </div>

      {loading && <div className="text-sm text-gray-medium py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading fleet telemetry…</div>}

      {!loading && machines.length > 0 && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="telemetry-table">
              <thead className="bg-gray-50 border-b text-xs text-gray-medium">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Machine</th>
                  <th className="text-left px-3 py-2 font-medium">Provider</th>
                  <th className="text-left px-3 py-2 font-medium">Device ID</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium">Vends</th>
                </tr>
              </thead>
              <tbody>
                {machines.map(m => (
                  <tr key={m.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`telemetry-row-${m.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium truncate max-w-[180px]">{m.chassis_serial}</div>
                      <div className="text-[11px] text-gray-500 truncate max-w-[180px]">{m.vendor} {m.model}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px] uppercase">{m.telemetry_provider}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-medium font-mono truncate max-w-[140px]">{m.telemetry_device_id || '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] ${m.status === 'deployed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-700'}`}>{m.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{m.total_vends || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {!loading && machines.length === 0 && (
        <Card><CardContent className="p-6 text-center text-sm text-gray-medium">
          No machines yet. Add machines from <b>Vending Fleet → Machines</b> to see telemetry stream here.
        </CardContent></Card>
      )}
    </div>
  );
}

function ProviderCard({ label, count, tone, icon }) {
  const toneStyles = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    sky: 'border-sky-200 bg-sky-50 text-sky-800',
    gray: 'border-gray-200 bg-gray-50 text-gray-700',
  };
  return (
    <Card className={toneStyles[tone] || toneStyles.gray}>
      <CardContent className="p-4 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">{count}</div>
          <div className="text-[10px] opacity-70 mt-0.5">machines</div>
        </div>
        <div className="opacity-60 flex-shrink-0">{icon}</div>
      </CardContent>
    </Card>
  );
}
